import { IonCol, IonGrid, IonRow } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import ReactFlow, { Background, Controls, Edge, Node } from 'reactflow';

import styles from '@components/automations/Automation.module.css';
import AutomationActionBlockNode from '@components/automations/nodes/AutomationActionBlockNode';
import AutomationConditionBlockNode from '@components/automations/nodes/AutomationConditionBlockNode';
import AutomationTerminatorBlockNode from '@components/automations/nodes/AutomationTerminatorBlockNode';
import AutomationTimingBlockNode from '@components/automations/nodes/AutomationTimingBlockNode';
import AutomationTriggerBlockNode from '@components/automations/nodes/AutomationTriggerBlockNode';
import AutomationPanel from '@components/automations/panels/AutomationPanel';
import { Block, Selection } from '@typing/Automations';
import { AutomationBlockOutputEnum, AutomationFragment as AutomationType } from '@typing/Generated';
import { layoutAutomation } from '@utils/automationUtils';

import 'reactflow/dist/style.css';

type Props = {
  automation: AutomationType;
};

const nodeTypes = {
  AutomationActionBlockNode,
  AutomationConditionBlockNode,
  AutomationTerminatorBlockNode,
  AutomationTimingBlockNode,
  AutomationTriggerBlockNode
};

const Automation = ({ automation }: Props) => {
  const intl = useIntl();
  const [selection, setSelection] = useState<Selection>();
  const [needsRerender, setNeedsRerender] = useState<boolean>(false);

  const handleClearSelection = useCallback(() => {
    setSelection(undefined);
  }, []);

  const handleBlockAdded = useCallback((block: Block) => {
    setSelection({ block, id: block.id });
  }, []);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (needsRerender) {
      timeout = setTimeout(() => setNeedsRerender(false), 500);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [needsRerender]);

  useEffect(() => {
    const timeout = setTimeout(() => setNeedsRerender(true), 500);

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [automation]);

  const handleSelection = useCallback(
    ({ nodes }: { nodes: Node[] }) => {
      if (nodes.length === 1) {
        const node = nodes[0];
        if (node.type === 'AutomationTerminatorBlockNode') {
          setSelection({
            id: node.id,
            terminator: { parentBlock: node.data.parent, parentOutput: node.data.parentOutput }
          });
        } else {
          setSelection({ block: automation.blocks.find(block => block.id === node.id) as Block, id: node.id });
        }
      }
    },
    [automation.blocks]
  );

  const handleNodeClicked = useCallback(
    (_event: any, node: Node) => {
      handleSelection({ nodes: [node] });
    },
    [handleSelection]
  );

  const addTerminatorParts = useCallback(
    (parent: Block, output: AutomationBlockOutputEnum, edges: Edge[], nodes: Node[]) => {
      nodes.push({
        data: { parent, parentOutput: output },
        id: `${parent.id}-terminator-${output}`,
        position: { x: 0, y: 0 },
        type: 'AutomationTerminatorBlockNode'
      });

      let label: string | null = null;
      if (output === AutomationBlockOutputEnum.CONDITION_FALSE) {
        label = intl.formatMessage({ id: 'models.automation.conditionFalseOutput' });
      } else if (output === AutomationBlockOutputEnum.CONDITION_TRUE) {
        label = intl.formatMessage({ id: 'models.automation.conditionTrueOutput' });
      }

      edges.push({
        id: `edges-${parent.id}-terminator-${output}`,
        label,
        source: parent.id,
        sourceHandle: output,
        target: `${parent.id}-terminator-${output}`,
        targetHandle: 'input'
      });
    },
    [intl]
  );

  const addPartsForBlock = useCallback(
    (block: Block, edges: Edge[], nodes: Node[]) => {
      nodes.push({
        data: { block },
        id: block.id,
        position: { x: 0, y: 0 },
        type: `${block.__typename}Node`
      });

      if (
        block.__typename === 'AutomationActionBlock' ||
        block.__typename === 'AutomationTimingBlock' ||
        block.__typename === 'AutomationTriggerBlock'
      ) {
        if (block.childBlock) {
          const childId = block.childBlock?.id;

          edges.push({
            id: `edges-${block.id}-${childId}`,
            source: block.id,
            sourceHandle: 'output',
            target: childId,
            targetHandle: 'input'
          });
        } else {
          if (block.__typename === 'AutomationActionBlock') {
            addTerminatorParts(block, AutomationBlockOutputEnum.ACTION, edges, nodes);
          } else if (block.__typename === 'AutomationTimingBlock') {
            addTerminatorParts(block, AutomationBlockOutputEnum.TIMING, edges, nodes);
          } else {
            addTerminatorParts(block, AutomationBlockOutputEnum.TRIGGER, edges, nodes);
          }
        }
      } else {
        if (block.childBlockTrue) {
          const childId = block.childBlockTrue?.id;

          edges.push({
            id: `edges-${block.id}-${childId}`,
            label: intl.formatMessage({ id: 'models.automation.conditionTrueOutput' }),
            source: block.id,
            sourceHandle: 'CONDITION_TRUE',
            target: childId,
            targetHandle: 'input'
          });
        } else {
          addTerminatorParts(block, AutomationBlockOutputEnum.CONDITION_TRUE, edges, nodes);
        }
        if (block.childBlockFalse) {
          const childId = block.childBlockFalse?.id;

          edges.push({
            id: `edges-${block.id}-${childId}`,
            label: intl.formatMessage({ id: 'models.automation.conditionFalseOutput' }),
            source: block.id,
            sourceHandle: 'CONDITION_FALSE',
            target: childId,
            targetHandle: 'input'
          });
        } else {
          addTerminatorParts(block, AutomationBlockOutputEnum.CONDITION_FALSE, edges, nodes);
        }
      }
    },
    [addTerminatorParts, intl]
  );

  const calculateFlowParts = () => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    automation.blocks.forEach(block => {
      addPartsForBlock(block, edges, nodes);
    });

    const rerenderNeeded = layoutAutomation(nodes, edges);

    if (rerenderNeeded && !needsRerender) {
      setNeedsRerender(true);
    }

    nodes.forEach(node => {
      node.selected = selection?.id === node.id;
    });

    return { edges, nodes };
  };

  const flowParts = calculateFlowParts();

  return (
    <IonGrid className={`${styles.fullPageGrid} ion-no-margin ion-no-padding`}>
      <IonRow className="ion-no-margin ion-no-padding">
        <IonCol size="8">
          <ReactFlow
            connectOnClick={false}
            edges={flowParts.edges}
            nodeTypes={nodeTypes}
            nodes={flowParts.nodes}
            onNodeClick={handleNodeClicked}
            onPaneClick={handleClearSelection}
            onSelectionChange={handleSelection}
            panOnScroll={true}
            proOptions={{ account: 'test', hideAttribution: true }}
            zoomOnPinch={true}
            zoomOnScroll={false}
          >
            <Background />
            <Controls showInteractive={false} />
          </ReactFlow>
        </IonCol>
        <IonCol className={styles.panel} size="4">
          <AutomationPanel automation={automation} onBlockAdded={handleBlockAdded} selection={selection} />
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

export default Automation;
